import { checkIsAlternativeVariantsChanged } from '@pages/AdditionalServices/tabs/DeliveryForm/components/RoutesCalculationWidget/helpers';
import { FclFtlTransport } from '@pages/AdditionalServices/tabs/DeliveryForm/components/RoutesCalculationWidget/TransportTypesParameters/types';
import DELIVERY_FORM_CONSTANTS from '@pages/AdditionalServices/tabs/DeliveryForm/consts';
import { GraphQLQuery } from '@settings/services/graphQLClient/GraphQLClient';
import gql from 'graphql-tag';

import { RouteCalculationParams } from './interfaces';

// Запрос расчета данных агрегации по маршрутам на основе переданных параметров
export class RouteAggregationQuery implements GraphQLQuery<null> {
  readonly query: any;

  readonly variables: null;

  constructor(
    params: RouteCalculationParams,
    isArchive: boolean,
    taskId: string,
    isSearchByTransportParams: boolean
  ) {
    this.variables = null;
    const containerParameters: string[] =
      params.cargoType === 'container'
        ? params.containerParameters?.containerParameters.map((container) => {
            const pallets = container.palletsLoaded.map(
              (pallet) => `{
				type: ${pallet.type},
				quantity: ${pallet.quantity},
				weight: ${pallet.weight}
			  }`
            );
            return `{
				containerId: ${container.containerId},
				containerQuantity: ${container.containerQuantity},
				eachContainerWeight: ${container.eachContainerWeight},
				isNeedContainerRent: ${params.isNeedContainerRent},
				withPallets: ${container.withPallets ? 'true' : 'false'},
				palletsLoaded: [${pallets.join(',')}]
      		}`;
          }) || []
        : [];

    const truckParameters: string[] =
      params.cargoType === 'auto'
        ? params.truckParameters?.truckParameters.map((truck) => {
            const pallets = truck.palletsLoaded.map(
              (pallet) => `{
				type: ${pallet.type},
				quantity: ${pallet.quantity},
				weight: ${pallet.weight}
			  }`
            );
            return `{
				truckId: ${truck.truckId},
				truckQuantity: ${truck.truckQuantity},
				eachTruckWeight: ${truck.eachTruckWeight},
				withPallets: ${truck.withPallets ? 'true' : 'false'},
				palletsLoaded: [${pallets.join(',')}]
			}`;
          }) || []
        : [];

    const customCargoParameters: string[] = ['custom', 'air', 'fclFtl'].includes(params.cargoType)
      ? params.customCargoParameters?.customCargoParameters.map((cargo) => {
          const type =
            cargo.parametersCargoCalcType === 'base'
              ? cargo.parametersType
              : cargo.parametersCargoCalcType;
          return `{
              customCargoType: ${type},
              baseParameters: {
                quantity: ${cargo.baseParameters.quantity},
                length: ${cargo.baseParameters.length},
                width: ${cargo.baseParameters.width},
                height: ${cargo.baseParameters.height},
                weight: ${cargo.baseParameters.weight}
              },
              palletParameters:{
                type: ${cargo.palletParameters.type}
                quantity: ${cargo.palletParameters.quantity}
                normativeHeightOfStacking: ${cargo.palletParameters.normativeHeightOfStacking}
                weight: ${cargo.palletParameters.weight}
              },
              volumeParameters:{
                volume: ${cargo.volumeParameters.volume}
                weight: ${cargo.volumeParameters.weight}
              }
            }`;
        }) || []
      : [];

    const toLocationType =
      params.endLocation?.type === 'terminal' && params.endLocation?.symbolCode === 'ANY'
        ? 'location'
        : params.endLocation?.type;
    const fromLocationType =
      params.startLocation?.type === 'terminal' && params.startLocation?.symbolCode === 'ANY'
        ? 'location'
        : params.startLocation?.type;

    let isDangerousCargo =
      params.cargoType === 'container'
        ? params.containerParameters?.isDangerousCargo
        : params.customCargoParameters?.isDangerousCargo;

    if (params.cargoType === 'auto') {
      isDangerousCargo = params.truckParameters?.isDangerousCargo;
    }

    let targetCurrencyId =
      params.cargoType === 'container'
        ? params.containerParameters?.targetCurrencyId
        : params.customCargoParameters?.targetCurrencyId;

    if (params.cargoType === 'auto') {
      targetCurrencyId = params.truckParameters?.targetCurrencyId;
    }

    let cargoType = params.cargoType === 'container' ? 'container' : 'custom';

    if (params.cargoType === 'auto') {
      cargoType = 'truck';
    }

    const alternativeRouteParams = checkIsAlternativeVariantsChanged(
      params,
      DELIVERY_FORM_CONSTANTS.routeParams
    )
      ? `alternativeRoutesParam: {
	  	withoutOnCarriage: ${params.isWithoutOnkeridge},
		fromTerminalRadius: ${params.isFromTerminalRadius},
		toTerminalRadius: ${params.isToTerminalRadius},
		withPreCarriage: ${params.isNeedPrekeridge},
		withoutPreCarriage: ${params.isWithoutPrekeridge},
		withCrossDocking: ${params.isWithCrossDocking},
	  }`
      : null;

    const cargoFingerprint =
      params.customCargoParameters.cargoFingerprint.length > 0
        ? `cargoFingerprint: "${params.customCargoParameters.cargoFingerprint}",`
        : null;

    const containerTransportTypes: FclFtlTransport[] = [];
    const truckTransportTypes: FclFtlTransport[] = [];

    params.customCargoParameters.transportTypes.forEach((transportType) => {
      if (transportType.type === 'container') {
        containerTransportTypes.push(transportType);
        return;
      }
      truckTransportTypes.push(transportType);
    });

    const containerTypes =
      containerTransportTypes.length > 0
        ? `containerTypes: [${containerTransportTypes
            .map(
              (container) => `{
					transportTypeId: ${container.transportTypeId},
					eachWeight: ${container.weight},
					quantity: 1
			}`
            )
            .join(',')}],`
        : null;

    const truckTypes =
      truckTransportTypes.length > 0
        ? `truckTypes: [${truckTransportTypes
            .map(
              (truck) => `{
					transportTypeId: ${truck.transportTypeId},
					eachWeight: ${truck.weight},
					quantity: 1
			}`
            )
            .join(',')}],`
        : null;

    const searchByTransportParams = isSearchByTransportParams
      ? `searchByTransportParams: true,`
      : null;

    const mixedTransportParams =
      params.cargoType === 'fclFtl'
        ? `mixedTransport: {
				enabled: true,
				${cargoFingerprint ?? ''}
				${containerTypes ?? ''}
				${truckTypes ?? ''}
				${searchByTransportParams ?? ''}
			}`
        : null;

    this.query = gql`
			query __AGGREGATION_ROUTES_QUERY__ {
				data: aggregateRoutesData(
					cargoParameters: {
						${alternativeRouteParams ?? ''}
						endTransportingConditionId: ${params.endTransportingCondition},
						isArchiveCalculation: ${isArchive ? 'true' : 'false'},
		  				isNeedPreCarriage: ${params.isNeedPrekeridge ? 'true' : 'false'},
		  				isDangerousCargo: ${isDangerousCargo},
						requiredTransportingTypes: [${params.requiredTransportingTypes.join(',')}], 
						date: "${params.startExpeditionDate?.toISOString()}",
						customCargoParameters: [
							${customCargoParameters.join(',')}
						], 
						startTransportingConditionId: ${params.startTransportingCondition},
						cargoType: ${cargoType}, 
						containerParameters: [
							${containerParameters.join(',')}
						],
						truckParameters: [
							${truckParameters.join(',')}
						],
						routePoints: {
							from: {${fromLocationType}: ${params.startLocation?.id}},
							to: {${toLocationType}: ${params.endLocation?.id}}
						},
		  				targetCurrencyId: ${targetCurrencyId},
						${mixedTransportParams ?? ''}
					},
					taskId: ${taskId}                 
				) {
					carriers
					contractors
					deliveryTime {
						max
						min
					}
					price {
						max
						min
					}
					transportingTypes
					terminals
					startTerminals
					endTerminals
					alternativeRoutes {
			  			exactSearch
						fromTerminalRadius
						toTerminalRadius
						withPreCarriage
						withoutOnCarriage
						withoutPreCarriage
						withCrossDocking
					}
				}
			}
  `;
  }
}
